import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { listTimeZones, findTimeZone, getZonedTime } from 'timezone-support';
import { formatZonedTime } from 'timezone-support/dist/parse-format';
import { formatToTimeZone } from 'date-fns-timezone';
import { TimezoneInfo } from '../../shared/types/TimezoneInfo';
import { LocationService } from '../../shared/location.service';
import { CountryDto } from '../../shared/types/CountryDto';
import { StateDto } from '../../shared/types/StateDto';
import { CityDto } from '../../shared/types/CityDto';
import { PhoneDto } from '../../shared/types/PhoneDto';
import { ProfileService } from '../../shared/profile.service';
import { ContactDto } from '../../shared/types/ContactDto';
import { forkJoin, of } from 'rxjs';
import { ContactPhoneType } from '../../shared/types/ContactPhoneDto';
import { UtilsService } from '../../shared/utils.service';
import { PropertyPatternValidation } from '../../shared/form-validations/custom-validators';
import { catchError, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'lp2-contact-info',
  templateUrl: './contact-info.component.html',
  styleUrls: ['./contact-info.component.scss'],
})
export class ContactInfoComponent implements OnInit {
  contactForm: FormGroup;

  timezones: TimezoneInfo[] = [];

  countries: CountryDto[] = [];

  states: StateDto[] = [];

  cities: CityDto[] = [];

  phoneInfo: PhoneDto;

  contactData: ContactDto;

  currentDateTime: string;

  newDateTime: string;

  contactPhoneType = ContactPhoneType;

  countryCode: string;

  primaryPhonePattern: string;

  secondaryPhonePattern: string;

  isRequesting = false;

  savedDisplayed = false;

  constructor(
    private profileService: ProfileService,
    private locationService: LocationService,
  ) {
    this.contactForm = new FormGroup({
      street1: new FormControl('', [Validators.required]),
      street2: new FormControl(''),
      postalCode: new FormControl('', [Validators.maxLength(20)]),
      timezone: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      state: new FormControl(''),
      city: new FormControl(''),
      primaryPhoneType: new FormControl(ContactPhoneType.CELL),
      primaryPhone: new FormControl('', [Validators.required]),
      secondaryPhoneType: new FormControl(ContactPhoneType.CELL),
      secondaryPhone: new FormControl(''),
    });
  }

  get street1(): AbstractControl {
    return this.contactForm.get('street1');
  }

  get timezone(): AbstractControl {
    return this.contactForm.get('timezone');
  }

  get country(): AbstractControl {
    return this.contactForm.get('country');
  }

  get state(): AbstractControl {
    return this.contactForm.get('state');
  }

  get city(): AbstractControl {
    return this.contactForm.get('city');
  }

  get primaryPhoneType(): AbstractControl {
    return this.contactForm.get('primaryPhoneType');
  }

  get secondaryPhoneType(): AbstractControl {
    return this.contactForm.get('secondaryPhoneType');
  }

  get primaryPhone(): AbstractControl {
    return this.contactForm.get('primaryPhone');
  }

  get secondaryPhone(): AbstractControl {
    return this.contactForm.get('secondaryPhone');
  }

  ngOnInit() {
    this.setTimezoneList();

    this.loadContactData();

    this.contactForm
      .get('timezone')
      .valueChanges.subscribe((value: TimezoneInfo) => {
        if (value && value.code !== this.contactData.timezone) {
          this.newDateTime = formatToTimeZone(
            new Date(),
            'dddd, DD MMM YYYY - h:mm a',
            { timeZone: value.code }
          );
        } else {
          this.newDateTime = '';
        }
      });

    this.contactForm
      .get('country')
      .valueChanges.subscribe((value: CountryDto) => {
        if (
          value &&
          (!this.states.length || this.states[0].countryId !== value.countryId)
        ) {
          this.phoneInfo = null;
          this.locationService
            .getStates(value.countryId)
            .subscribe((data: StateDto[]) => {
              this.states = data;
              this.setStateValidators();

              if (!data.length) {
                this.getPhoneInfo(value.phone.phoneId);
              }
            });
        }
      });

    this.contactForm.get('state').valueChanges.subscribe((value: StateDto) => {
      if (
        value &&
        (!this.cities.length || this.cities[0].stateId !== value.stateId)
      ) {
        this.phoneInfo = null;
        this.locationService
          .getCities(value.stateId)
          .subscribe((data: CityDto[]) => {
            this.cities = data;
            this.setCityValidators();

            if (!data.length) {
              this.getPhoneInfo(value.phoneId);
            }
          });
      }
    });

    this.contactForm.get('city').valueChanges.subscribe((value: CityDto) => {
      this.phoneInfo = null;
      if (value) {
        this.getPhoneInfo(value.phoneId);
      }
    });

    this.contactForm
      .get('primaryPhoneType')
      .valueChanges.subscribe((value: CityDto) => {
        this.getPrimaryPhoneExtraData();
      });

    this.contactForm
      .get('secondaryPhoneType')
      .valueChanges.subscribe((value: CityDto) => {
        this.getSecondaryPhoneExtraData();
      });
  }

  getPhoneInfo(phoneId: number) {
    if (phoneId) {
      this.locationService.getPhoneInfo(phoneId).subscribe((data: PhoneDto) => {
        this.phoneInfo = data;

        this.countryCode = this.getCountryCode(
          this.phoneInfo.mobilePhoneCallFormat
        );

        this.getPrimaryPhoneExtraData();
        this.getSecondaryPhoneExtraData();
      });
    }
  }

  getPrimaryPhoneExtraData() {
    if (this.phoneInfo) {
      this.primaryPhonePattern =
        this.primaryPhoneType.value === this.contactPhoneType.CELL
          ? this.phoneInfo.mobilePhoneFormat
          : this.phoneInfo.linePhoneFormat;

      if (this.primaryPhonePattern) {
        this.primaryPhone.setValidators([
          Validators.required,
          PropertyPatternValidation(
            'data',
            UtilsService.convertMaskToRegEx(
              `${this.countryCode}${this.primaryPhonePattern}`
            )
          ),
        ]);
      }
    }
  }

  getSecondaryPhoneExtraData() {
    if (this.phoneInfo) {
      this.secondaryPhonePattern =
        this.secondaryPhoneType.value === this.contactPhoneType.CELL
          ? this.phoneInfo.mobilePhoneFormat
          : this.phoneInfo.linePhoneFormat;

      if (this.secondaryPhonePattern) {
        this.secondaryPhone.setValidators([
          PropertyPatternValidation(
            'data',
            UtilsService.convertMaskToRegEx(
              `${this.countryCode}${this.secondaryPhonePattern}`
            ),
            'userEnteredData'
          ),
        ]);
      }
    }
  }

  getCountryCode(pattern: string) {
    const regExp: RegExp = /\(([^)]+)\)/;
    const matches = regExp.exec(pattern);
    return matches && matches.length > 1 ? matches[1] : '';
  }

  setStateValidators() {
    this.state.setValue('');
    this.cities = [];
    this.city.setValue('');
    this.city.clearValidators();

    if (this.states.length) {
      this.state.setValidators(Validators.required);
      this.state.updateValueAndValidity();
    }
  }

  setCityValidators() {
    this.city.setValue('');
    this.city.clearValidators();

    if (this.cities.length) {
      this.city.setValidators(Validators.required);
      this.city.updateValueAndValidity();
    }
  }

  loadContactData() {
    this.profileService.getContactData().pipe(
      switchMap((data: ContactDto) => this.locationService.getCity(data.address.cityId).pipe(
        map((city: CityDto) => {
          data.address.stateId = city.stateId;
          return data;
        }),
        catchError(() => of(data))
      ))
    ).subscribe((data: ContactDto) => {
      this.contactData = data;

      // Preload states and cities if needed
      const dataToLoad: any[] = [this.locationService.getCountries()];
      if (data && data.address && data.address.stateId) {
        dataToLoad.push(this.locationService.getStates(data.address.countryId));
        if (data.address.cityId) {
          dataToLoad.push(this.locationService.getCities(data.address.stateId));
        }
      }

      forkJoin(dataToLoad).subscribe(
        (results: [CountryDto[], StateDto[], CityDto[]]) => {
          this.countries = results[0];

          if (results.length > 1) {
            this.states = results[1];
            if (results.length > 2) {
              this.cities = results[2];
            }
          }

          // Preload phone data if needed
          if (this.contactData.address) {
            let phoneId: number;

            if (
              this.contactData.address.stateId &&
              !this.contactData.address.cityId
            ) {
              phoneId = this.states.find(
                (e) => e.stateId === this.contactData.address.stateId
              ).phoneId;
            } else if (this.contactData.address.countryISO) {
              phoneId = this.countries.find(
                (e) => e.isoCode === this.contactData.address.countryISO
              ).phone.phoneId;
            }

            if (phoneId !== undefined) {
              this.getPhoneInfo(phoneId);
            }
          }

          // Wait to render the controls
          setTimeout(() => {
            this.patchForm();
          });
        }
      );
    });
  }

  patchForm() {
    if (this.contactData) {
      if (this.contactData.timezone) {
        this.currentDateTime = formatToTimeZone(
          new Date(),
          'dddd, DD MMM YYYY - h:mm a',
          { timeZone: this.contactData.timezone }
        );
      }

      if (this.contactData.address) {
        this.contactForm.patchValue({
          street1: this.contactData.address.street1,
          street2: this.contactData.address.street2,
          postalCode: this.contactData.address.postalCode,
          timezone: this.contactData.timezone
            ? this.timezones.find((e) => e.code === this.contactData.timezone)
            : '',
          country:
            this.contactData.address && this.contactData.address.countryISO
              ? this.countries.find(
                  (e) => e.isoCode === this.contactData.address.countryISO
                )
              : '',
          state:
            this.contactData.address && this.contactData.address.stateId
              ? this.states.find(
                  (e) => e.stateId === this.contactData.address.stateId
                )
              : '',
          city:
            this.contactData.address && this.contactData.address.cityId
              ? this.cities.find(
                  (e) => e.cityId === this.contactData.address.cityId
                )
              : '',
          primaryPhoneType:
            this.contactData.primaryPhone &&
            this.contactData.primaryPhone.userEnteredData
              ? this.contactData.primaryPhone.contactType
              : ContactPhoneType.CELL,
          primaryPhone: {
            data:
              this.contactData.primaryPhone &&
              this.contactData.primaryPhone.data
                ? this.contactData.primaryPhone.data
                : '',
            userEnteredData:
              this.contactData.primaryPhone &&
              this.contactData.primaryPhone.userEnteredData
                ? this.contactData.primaryPhone.userEnteredData
                : '',
          },
          secondaryPhoneType:
            this.contactData.secondaryPhone &&
            this.contactData.secondaryPhone.userEnteredData
              ? this.contactData.secondaryPhone.contactType
              : ContactPhoneType.CELL,
          secondaryPhone: {
            data:
              this.contactData.secondaryPhone &&
              this.contactData.secondaryPhone.data
                ? this.contactData.secondaryPhone.data
                : '',
            userEnteredData:
              this.contactData.secondaryPhone &&
              this.contactData.secondaryPhone.userEnteredData
                ? this.contactData.secondaryPhone.userEnteredData
                : '',
          },
        });
      }
    }
  }

  setTimezoneList() {
    const timezones = listTimeZones();
    this.timezones = [];
    const nowDate = new Date();

    for (const zone of timezones) {
      // Filter out short timezones
      if (zone.indexOf('Etc') !== 0 && zone.indexOf('/') !== -1) {
        const tzInfo = findTimeZone(zone);
        const offset = formatZonedTime(getZonedTime(nowDate, tzInfo), 'Z');

        this.timezones.push({
          value: `(UTC${offset}) ${zone.replace(/_/g, ' ')}`,
          code: zone,
          offset: offset,
        });
      }
    }

    this.timezones = this.timezones.sort((a: TimezoneInfo, b: TimezoneInfo) => {
      const offsetA = a.offset.split(':');
      const minutesA = +offsetA[0] * 60 + +offsetA[1];

      const offsetB = b.offset.split(':');
      const minutesB = +offsetB[0] * 60 + +offsetB[1];

      if (minutesA < minutesB) {
        return -1;
      }

      if (minutesA > minutesB) {
        return 1;
      }

      return 0;
    });
  }

  save() {
    this.isRequesting = true;

    this.contactData.address.street1 = this.street1.value;
    this.contactData.address.street2 = this.contactForm.get('street2').value;
    this.contactData.address.postalCode = this.contactForm.get(
      'postalCode'
    ).value;
    this.contactData.timezone = this.timezone.value.code;

    const country: CountryDto = this.contactForm.get('country').value;
    this.contactData.address.countryISO = country.isoCode;
    this.contactData.address.countryId = country.countryId;
    this.contactData.address.countryCode = country.isoCode;
    this.contactData.address.countryName = country.countryName;

    const state: StateDto = this.contactForm.get('state').value;
    this.contactData.address.stateId = state ? state.stateId : null;
    this.contactData.address.stateName = state ? state.stateName : null;
    this.contactData.address.state = state ? state.stateName : null;

    const city: CityDto = this.contactForm.get('city').value;
    this.contactData.address.cityId = city ? city.cityId : null;
    this.contactData.address.cityName = city ? city.cityName : null;
    this.contactData.address.city = city ? city.cityName : null;

    const primary = this.primaryPhone.value;
    this.contactData.primaryPhone.contactType = this.primaryPhoneType.value;
    this.contactData.primaryPhone.data = primary.data;
    this.contactData.primaryPhone.updatedDate = Date.now();
    this.contactData.primaryPhone.userEnteredData = primary.userEnteredData;

    const secondary = this.secondaryPhone.value;
    if (secondary && secondary.userEnteredData) {
      this.contactData.secondaryPhone.contactType = this.secondaryPhoneType.value;
      this.contactData.secondaryPhone.data = secondary.data;
      this.contactData.secondaryPhone.updatedDate = Date.now();
      this.contactData.secondaryPhone.userEnteredData =
        secondary.userEnteredData;
    } else {
      this.contactData.secondaryPhone.contactType = ContactPhoneType.CELL;
      this.contactData.secondaryPhone.data = '';
      this.contactData.secondaryPhone.userEnteredData = '';
    }

    this.profileService.saveContactData(this.contactData).subscribe(() => {
      this.isRequesting = false;
      this.savedDisplayed = true;
      this.contactForm.markAsPristine();

      setTimeout(() => (this.savedDisplayed = false), 3000);
    });
  }
}



// WEBPACK FOOTER //
// ./ts/app/preferences/contact-info/contact-info.component.ts